var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.compileFieldResolver = exports.computeFinalArgs = void 0;
const hooks_1 = require("../../hooks/hooks");
const SchemaRoot_1 = require("../../schema/SchemaRoot");
const Inject_1 = require("../../inject/Inject");
const registry_1 = require("../../arg/registry");
const getParameterNames_1 = require("../../../services/utils/getParameterNames");
const class_transformer_1 = require("class-transformer");
const parseNative_1 = require("../../../services/utils/gql/types/parseNative");
const isPromiseLike_1 = __importDefault(require("../../../isPromiseLike"));
async function performHooksExecution(hooks, injectorData) {
    // all hooks are executed in parallel. Resolution of the field continues after the hooks resolve all their promises
    return Promise.all(hooks.map((hook) => {
        return hook(injectorData);
    }));
}
async function performAfterHooksExecution(hooks, injectorData, resolvedValue) {
    // all hooks are executed in parallel. Resolution of the field continues after the hooks resolve all their promises
    return Promise.all(hooks.map((hook) => {
        return hook(resolvedValue, injectorData);
    }));
}
function resolveExplicitArgument(argConfig, argValue) {
    if (Array.isArray(argConfig.type)) {
        const type = argConfig.type[0];
        if (!type) {
            return argValue;
        }
        return argValue.map((singleArg) => {
            if (typeof singleArg !== 'object' || !type.prototype) {
                return singleArg;
            }
            const instance = Object.create(type.prototype);
            return Object.assign(instance, singleArg);
        });
    }
    else {
        const { type } = argConfig;
        if (typeof argValue !== 'object' ||
            !type.prototype ||
            argValue.__proto__ === type.prototype // we only want to do the cast into an instance of the explicit type if it isn't already an instance. This can happen when GQL scalar does it-for example GraphQLDateTime
        ) {
            return argValue;
        }
        const instance = Object.create(type.prototype);
        return Object.assign(instance, argValue);
    }
}
function resolveReflectedArgument(reflectedType, argValue) {
    if (typeof reflectedType === 'function' && !parseNative_1.isParsableScalar(reflectedType)) {
        const instance = Object.create(reflectedType.prototype);
        return Object.assign(instance, argValue);
    }
    return argValue;
}
function computeFinalArgs(func, { args, injectors, injectorToValueMapper, getArgConfig, reflectedParamTypes }) {
    const paramNames = getParameterNames_1.getParameterNames(func);
    return paramNames.map((paramName, index) => {
        const argConfig = getArgConfig(index);
        if (args && args.hasOwnProperty(paramName)) {
            const argValue = args[paramName];
            if (argValue === null) {
                return argValue;
            }
            const reflectedType = reflectedParamTypes && reflectedParamTypes[index];
            if (argConfig && argConfig.type) {
                return resolveExplicitArgument(argConfig, argValue);
            }
            else if (reflectedType) {
                return resolveReflectedArgument(reflectedType, argValue);
            }
            else {
                return argValue;
            }
        }
        if (argConfig) {
            return args[argConfig.name];
        }
        const injector = injectors[index];
        if (!injector) {
            return undefined;
        }
        return injectorToValueMapper(injector);
    });
}
exports.computeFinalArgs = computeFinalArgs;
function getFieldOfTarget(instance, prototype, fieldName) {
    if (!instance) {
        return prototype[fieldName];
    }
    const instanceField = instance[fieldName];
    if (instanceField !== undefined) {
        return instanceField;
    }
    return prototype[fieldName];
}
function compileFieldResolver(target, fieldName, castTo) {
    function castIfNeeded(result) {
        if (castTo && result !== null && typeof result === 'object') {
            if (castTo.name === 'castTo' || castTo.name === 'itemCast') {
                // this function is a thunk, so we get the type now
                castTo = castTo();
            }
            if (Array.isArray(castTo)) {
                if (!Array.isArray(result)) {
                    throw new TypeError(`field ${fieldName} castTo is an array, yet it resolves with ${result} which is ${typeof result}`);
                }
                return result.map((item) => {
                    if (Array.isArray(item)) {
                        console.error('array cannot be casted as object type: ', item);
                        throw new TypeError(`field "${fieldName}" cannot be casted to object type ${castTo[0].name} - returned value is an array`);
                    }
                    return class_transformer_1.plainToClass(castTo[0], item);
                });
            }
            else {
                return class_transformer_1.plainToClass(castTo, result);
            }
        }
        return result;
    }
    const injectors = Inject_1.injectorRegistry.getAll(target)[fieldName];
    const beforeHooks = hooks_1.fieldBeforeHooksRegistry.get(target, fieldName);
    const afterHooks = hooks_1.fieldAfterHooksRegistry.get(target, fieldName);
    return async (source, args = null, context = null, info = null) => {
        if (SchemaRoot_1.isSchemaRoot(target)) {
            source = SchemaRoot_1.getSchemaRootInstance(target);
        }
        const injectorData = {
            source,
            args,
            context,
            info
        };
        if (beforeHooks) {
            await performHooksExecution(beforeHooks, injectorData);
        }
        const instanceField = getFieldOfTarget(source, target.prototype, fieldName);
        let resolvedValue;
        if (typeof instanceField !== 'function') {
            resolvedValue = castIfNeeded(instanceField);
            if (afterHooks) {
                await performAfterHooksExecution(afterHooks, injectorData, resolvedValue);
            }
            return resolvedValue;
        }
        const instanceFieldFunc = instanceField;
        const params = computeFinalArgs(instanceFieldFunc, {
            args: args || {},
            reflectedParamTypes: Reflect.getMetadata('design:paramtypes', target.prototype, fieldName),
            injectors: injectors || {},
            injectorToValueMapper: (injector) => injector.apply(source, [{ source, args, context, info }]),
            getArgConfig: (index) => {
                return registry_1.argRegistry.get(target, [fieldName, index]);
            }
        });
        const promiseOrValue = instanceFieldFunc.apply(source, params);
        resolvedValue = isPromiseLike_1.default(promiseOrValue)
            ? await promiseOrValue
            : promiseOrValue;
        resolvedValue = castIfNeeded(resolvedValue);
        if (afterHooks) {
            await performAfterHooksExecution(afterHooks, injectorData, resolvedValue);
        }
        return resolvedValue;
    };
}
exports.compileFieldResolver = compileFieldResolver;
//# sourceMappingURL=resolver.js.map